import React, { useEffect, useState } from 'react';
import { activitiesAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Button, Modal, FormField, Empty, Alert, FilterPills } from '../components/ui';
import { Plus, Pencil, Trash2 } from 'lucide-react';

// Backend Activity model:
//   activity_id | user_id (auto JWT) | customer_id (REQUIRED) | type | subject | notes | date
// Type ENUM is LOWERCASE: call | email | meeting | note
// FIXES: types must be lowercase, customer_id required, added subject field

const TYPES = ['note', 'call', 'email', 'meeting'];
const TYPE_LABELS = { note: 'Note', call: 'Call', email: 'Email', meeting: 'Meeting' };
const typeColors = { note: '#a78bfa', call: '#60a5fa', email: '#34d399', meeting: '#fbbf24' };

const emptyForm = {
  type: 'note',        // lowercase!
  client_id: '',     // required!
  subject: '',         // separate subject field
  notes: '',           // notes field (not description)
  date: new Date().toISOString().split('T')[0],
};

export default function Activities() {
  const { canCreate, canUpdate, canDelete } = useAuth();
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    const requests = [activitiesAPI.list()];
    if (canCreate) requests.push(usersAPI.list());
    Promise.all(requests)
      .then(([a, c]) => { setActivities(a.data.data || []); if (canCreate) setUsers((c?.data?.data || []).filter(u => u.role === 'Client')); })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setError(''); setModal(true); };
  const openEdit = (act) => {
    setEditing(act);
    setForm({
      type:        act.type,                  // already lowercase from backend
      client_id: act.client_id,
      subject:     act.subject || '',
      notes:       act.notes || '',
      date:        act.date ? act.date.split('T')[0] : new Date().toISOString().split('T')[0],
    });
    setError(''); setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const payload = {
        type:        form.type,         // must be lowercase
        client_id: form.client_id,  // required
        subject:     form.subject,
        notes:       form.notes,
        date:        form.date,
      };
      if (editing) await activitiesAPI.update(editing.activity_id, payload);
      else await activitiesAPI.create(payload); // user_id auto-injected by backend JWT
      setModal(false); setForm(emptyForm); load();
    } catch (err) { setError(err.response?.data?.message || 'Error saving activity'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try { await activitiesAPI.delete(id); load(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const filtered = activities
    .filter(act => !filterType || act.type === filterType)
    .filter(act => !search || act.subject?.toLowerCase().includes(search.toLowerCase()) || act.client?.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="animate-in">
      <PageHeader title="Activities" subtitle={`${filtered.length} of ${activities.length}`} eyebrow="Timeline"
        action={canCreate ? <Button onClick={openCreate}><Plus size={15} />Add Activity</Button> : null} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search by subject or client..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 220px', minWidth: 180, maxWidth: 320, padding: '7px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13 }}
        />
        <FilterPills
          options={[
            { value: '', label: 'All' },
            { value: 'note', label: 'Note' },
            { value: 'call', label: 'Call' },
            { value: 'email', label: 'Email' },
            { value: 'meeting', label: 'Meeting' },
          ]}
          value={filterType}
          onChange={setFilterType}
        />
      </div>

      <Card>
        {loading
          ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          : filtered.length === 0
            ? <Empty message="No activities found" />
            : (
              <div style={{ padding: '4px 0' }}>
                {filtered.map((act, i) => (
                  <div key={act.activity_id} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '16px 0', borderBottom: i < activities.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 2 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: typeColors[act.type] || 'var(--accent)', flexShrink: 0 }} />
                      {i < filtered.length - 1 && <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 6, minHeight: 20 }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: typeColors[act.type] || 'var(--accent)', background: `${typeColors[act.type] || 'var(--accent)'}18`, padding: '2px 10px', borderRadius: 20, textTransform: 'capitalize' }}>
                          {TYPE_LABELS[act.type] || act.type}
                        </span>
                        {act.user?.name && <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 500 }}>{act.user.name}</span>}
                        {act.client?.name && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>→ {act.client.name}</span>}
                      </div>
                      {act.subject && <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{act.subject}</div>}
                      {act.notes && <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{act.notes}</div>}
                      <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>{new Date(act.date || act.createdAt).toLocaleString()}</div>
                    </div>
                    {(canUpdate || canDelete) && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {canUpdate && <Button size="sm" variant="ghost" onClick={() => openEdit(act)}><Pencil size={13} /></Button>}
                      {canDelete && <Button size="sm" variant="danger" onClick={() => handleDelete(act.activity_id)}><Trash2 size={13} /></Button>}
                    </div>
                    )}
                  </div>
                ))}
              </div>
            )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Activity' : 'Add Activity'}>
        <Alert type="error" message={error} />
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Type">
              <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}>
                {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </FormField>
            <FormField label="Client (required)">
              <select value={form.client_id} onChange={e => setForm(f => ({...f, client_id: e.target.value}))} required>
                <option value="">Select client...</option>
                {users.map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Subject">
            <input value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} placeholder="Brief subject line" />
          </FormField>
          <FormField label="Notes">
            <textarea rows={4} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="What happened? Add details..." style={{ resize: 'vertical' }} />
          </FormField>
          <FormField label="Date">
            <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} required />
          </FormField>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setModal(false)} type="button">Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Activity'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
